import React from 'react'
import { ThaiMeaningFullProps } from 'core/interfaces/dictionary-thai-interfaces'
import { availableVoices, ThaiVoiceGender, VOICE_ORDER, voiceLabel } from '../../../lib/sounds'
import SpeakerIcon from '../speaker-icon'
import { useTranslit } from 'hooks/useTranslit'
import { distinctNonEmpty } from '../../../lib/pronunciation'
import CommonnessCard from '../commonness-card'
import SenseNavigator from './sense-navigator'
import { usePartHover } from './part-hover-context'
import SaveWordButton from '../save-word-button'

type Props = {
   word: string
   // The compound's components, in order, when the page shows a breakdown. Used
   // to split the headword so hovering a component tints its chunk here.
   parts?: string[]
   // The word's senses; when there are >= 2 the nutshell shows an index-chip
   // row that jumps to each sense.
   meanings: ThaiMeaningFullProps[]
   phoneticSpaced: string
   // Heteronym readings from the word's senses (สระ "vowel" สะ-หฺระ beside the
   // word-level สะ), shown alongside the main reading. Empty/absent for the
   // ordinary one-reading word. See distinctReadings in lib/pronunciation.
   phoneticAlternates?: string[]
   // 0–1; drives the friendly tier label + meter in the hero's USAGE block.
   commonnessScore: number
   // Authored, word-specific summary (thai_words.commonness_blurb) shown as the
   // hero's "In a nutshell" line. null/absent hides the block.
   commonnessBlurb?: string | null
   hasSoundFile: boolean
   audioGenders?: ThaiVoiceGender[]
   playSound: (gender: ThaiVoiceGender) => Promise<void>
   separable?: { part1: string; part2: string } | null
}

// Split the headword into one chunk per component, so each can light up on its
// own. Components usually concatenate straight back into the headword, but not
// always (reordered or reworded parts) — anything that doesn't line up exactly,
// including a leftover tail, gives up and leaves the headword whole.
const splitByParts = (word: string, parts: string[] | undefined): string[] | null => {
   if (!parts || parts.length < 2) return null

   let offset = 0
   for (const part of parts) {
      if (!word.startsWith(part, offset)) return null
      offset += part.length
   }

   return offset === word.length ? [...parts] : null
}

const MainWordBar = (props: Props) => {
   const [playing, setPlaying] = React.useState<ThaiVoiceGender | null>(null)

   const translitOf = useTranslit()
   // A heteronym shows every reading it has, comma-separated ("sà, sà-rà"),
   // with the per-sense split spelled out down in the meanings list.
   const readings = distinctNonEmpty([props.phoneticSpaced, ...(props.phoneticAlternates ?? [])])
   const translit = distinctNonEmpty(readings.map((reading) => translitOf.of(reading))).join(', ')

   const blurb = props.commonnessBlurb?.trim()
   // Sense chips join the nutshell for polysemous words. Monosemous words show
   // no chip row (a single sense needs no index).
   const hasSenseChips = props.meanings.length >= 2

   const { hoveredPart } = usePartHover()
   const headwordChunks = splitByParts(props.word, props.parts)

   // The bracketed phonetic spelling only earns its place when it differs from
   // the headword; when it's the same Thai string (bar spacing) it's noise. A
   // second reading always differs, so a heteronym always shows the bracket.
   const bare = (value: string) => value.replace(/\s+/g, '')
   const showPhonetic = readings.some((reading) => bare(reading) !== bare(props.word))

   const handlePlay = (gender: ThaiVoiceGender) => {
      setPlaying(gender)
      props.playSound(gender).finally(() => {
         setPlaying((current) => (current === gender ? null : current))
      })
   }

   const availableGenders = availableVoices(props.audioGenders, props.hasSoundFile)

   return (
      <div>
         <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-x-[44px] gap-y-[28px] sm:gap-y-0 items-center">
            <div className="min-w-0 max-sm:order-1">
               <div className="flex items-center gap-[18px] flex-wrap">
                  <h1 className="font-thai text-[36px] min-[400px]:text-[44px] sm:text-[62px] text-bodyBlack font-medium leading-[1.1] m-0">
                     {headwordChunks
                        ? headwordChunks.map((chunk, index) => {
                             const isPart1 = index % 2 === 0
                             const isHot = hoveredPart === index
                             const washBg = isHot
                                ? isPart1
                                   ? 'bg-[linear-gradient(to_bottom,transparent_14%,var(--color-part1Bg)_14%,var(--color-part1Bg)_92%,transparent_92%)]'
                                   : 'bg-[linear-gradient(to_bottom,transparent_14%,var(--color-part2Bg)_14%,var(--color-part2Bg)_92%,transparent_92%)]'
                                : ''
                             return (
                                <span
                                   key={index}
                                   className={`rounded-[6px] px-[3px] -mx-[3px] transition-[background-image] duration-150 ${washBg}`}
                                >
                                   {chunk}
                                </span>
                             )
                          })
                        : props.word}
                  </h1>
                  <div className="flex gap-[10px] translate-y-[6px] sm:translate-y-[8px]">
                     {/* The hero teaches the two voices in words, so the caret
                         the lists use further down is already understood by the
                         time a reader meets it. One voice gives the pill a
                         single half; no voice and the headword sits alone. */}
                     {availableGenders.length > 0 && (
                        <div className="inline-flex items-stretch shrink-0 overflow-hidden rounded-full border border-hairline">
                           {VOICE_ORDER.filter((gender) => availableGenders.includes(gender)).map(
                              (gender, index) => (
                                 <React.Fragment key={gender}>
                                    {index > 0 && <span aria-hidden className="w-px bg-hairline" />}
                                    <button
                                       type="button"
                                       className={`flex items-center gap-[8px] px-[18px] py-[12px] border-none bg-transparent cursor-pointer transition-all duration-150 ${
                                          playing === gender
                                             ? 'text-lightBlue bg-usageBg'
                                             : 'text-speakerIdle hover:text-lightBlue hover:bg-usageBg focus-visible:text-lightBlue focus-visible:bg-usageBg'
                                       }`}
                                       title={`Listen — ${voiceLabel(gender).toLowerCase()} voice`}
                                       aria-label={`Listen — ${voiceLabel(gender).toLowerCase()} voice`}
                                       onClick={() => handlePlay(gender)}
                                    >
                                       <SpeakerIcon arcs={1} className="text-[20px] block" />
                                       <span className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-bodyBlack">
                                          {voiceLabel(gender)}
                                       </span>
                                    </button>
                                 </React.Fragment>
                              )
                           )}
                        </div>
                     )}
                     {/* After the listen buttons, matching the mockup's order. */}
                     <SaveWordButton lang="thai" word={props.word} />
                  </div>
               </div>

               {(translit || showPhonetic) && (
                  <div className="flex items-baseline gap-[16px] flex-wrap mt-[16px]">
                     {translit && (
                        <span className="font-translit text-[26px] sm:text-[29px] italic font-semibold text-translit">
                           {translit}
                        </span>
                     )}
                     {showPhonetic && (
                        <span className="font-thai text-[18px] sm:text-[20px] text-textMuted thai">
                           [ {readings.join(', ')} ]
                        </span>
                     )}
                  </div>
               )}

               {props.separable && (
                  <div className="font-thai text-[18px] text-bodyGray mt-[12px] thai">
                     also: {props.separable.part1} … {props.separable.part2}
                  </div>
               )}
            </div>

            <div className="flex flex-col gap-[14px] max-sm:order-3">
               <CommonnessCard commonnessScore={props.commonnessScore} showVerdict />
            </div>

            {/* Spans both columns, under the headword and the usage badge. */}
            {(blurb || hasSenseChips) && (
               <div className="col-span-full max-sm:order-2 max-sm:mt-0 bg-nutshellBg rounded-[10px] p-[12px_14px] sm:p-[14px_18px] mt-[26px] flex flex-col gap-[8px]">
                  {blurb && (
                     <>
                        <div className="text-[12px] font-bold tracking-[0.8px] uppercase text-coral">
                           In a nutshell
                        </div>
                        <div className="font-source-sans text-[19px] sm:text-[20px] font-normal not-italic text-bodyBlack leading-[1.5]">
                           {blurb}
                        </div>
                     </>
                  )}
                  {hasSenseChips && <SenseNavigator meanings={props.meanings} />}
               </div>
            )}
         </div>
      </div>
   )
}

export default MainWordBar
